import {
  IEMRStaticDurationResponseData,
  IEMRStaticFrequencyResponseData,
  IEMRStaticRoutesResponseData,
  IEMRStaticTimingsResponseData,
  IEMRStaticUnitsResponseData,
} from "@/api_framework/api_modals/EMRs";
import Input from "@/app/components/ui/Inputs/Input";
import InputWithSuggestionBox from "@/app/components/ui/Inputs/InputWithSuggestionBox";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { EMRModeType, ICustomerEMRState, setTaperingIndex } from "@/app_redux/reducers/slices/auth/customer_emr_slice";
import { TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { IEMRMedicationSectionFormFields } from "..";
import { useRef } from "react";
import InputWithStartAndEndAddOn from "@/app/components/ui/Inputs/InputWithStartAndEndAddOn";
import { toNumber } from "lodash";

const MEDICINE_DROPDOWN_Z_INDEX = 20;

const NestedTaperingDose: React.FC<{
  units: IEMRStaticUnitsResponseData[];
  frequencies: IEMRStaticFrequencyResponseData[];
  durations: IEMRStaticDurationResponseData[];
  timings: IEMRStaticTimingsResponseData[];
  routes: IEMRStaticRoutesResponseData[];
  mode: EMRModeType;
  formHook: UseFormReturn<IEMRMedicationSectionFormFields, any>;
  nestedIndex: number;
  // Tapering-dose fields mutate the form via setValue/append/remove, which RHF
  // watch reports with type undefined — parents that auto-save on user input
  // pass this so those edits still get persisted.
  onProgrammaticEdit?: () => void;
  dropdownZIndex?: number;
}> = ({ formHook, nestedIndex, mode, durations, frequencies, timings, units, onProgrammaticEdit,dropdownZIndex = MEDICINE_DROPDOWN_Z_INDEX}) => {
  const frequencyItems = useSelectMenuReducer(frequencies, "frequency_name", "frequency_name");
  const unitItems = useSelectMenuReducer(units, "dose_unit_name", "dose_unit_name");
  const timingsItems = useSelectMenuReducer(timings, "timing_name", "timing_name");
  const durationItems = useSelectMenuReducer(durations, "duration_name", "duration_name");
  const doseRef = useRef<HTMLInputElement>(null);
  const frequencyRef = useRef<HTMLInputElement>(null);
  const timingRef = useRef<HTMLInputElement>(null);
  const durationUnitRef = useRef<HTMLInputElement>(null);
  const remarkRef = useRef<HTMLInputElement>(null);
  const { forNumericWithDecimal } = useFormValidations();
  const { getRouteKey } = useRouter();
  const dispatch = useAppDispatch();
  const { taperingIndex } = useRouteInfo(getRouteKey("CUSTOMER_EMR", "id")).routeState.state as ICustomerEMRState;
  const fieldArrayHook = useFieldArray({
    name: `medications.${nestedIndex}.tapering_doses` as const,
    control: formHook.control,
  });

  useAfterFirstRender(() => {
    if (taperingIndex < 0) return;
    if (taperingIndex >= 0) {
      if (taperingIndex === nestedIndex) {
        fieldArrayHook.append({ quantity: 1 });
        onProgrammaticEdit?.();
        dispatch(setTaperingIndex(-1));
      }
    }
  }, [taperingIndex]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, nextRef: React.RefObject<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef && nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  return fieldArrayHook?.fields?.map((field, index) => {
    return (
      <tr id={field.id}>
        <td className="">
          <div className="w-full flex justify-end items-center text-xs text-amber-600 pr-2 ">
            Tapering Dosage {index + 1}
          </div>
        </td>
        <td className=" w-56">
          <div className="w-full pt-2 pb-2 pl-2">
            <div className="relative rounded-md flex items-center gap-3 px-3">
              {mode === "PDF_PREVIEW" ? (
                <span className="w-32 text-xs flex justify-start items-center">
                  {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.dose`)}{" "}
                  {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.dose_unit`)}
                </span>
              ) : (
                <Input
                  errorMessages={[{ message: "", type: "required" }]}
                  fieldError={
                    formHook?.formState?.errors
                      ? formHook?.formState?.errors?.medications
                        ? formHook?.formState?.errors?.medications?.[nestedIndex]
                          ? formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses
                            ? formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]
                              ? formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.dose
                                ? formHook?.formState?.errors?.medications?.[index]?.dose
                                : null
                              : null
                            : null
                          : null
                        : null
                      : null
                  }
                  className="w-20 border-[#E2E2E2] "
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  variant="TRANSPARENT"
                  inputClassName="ring-1 focus:border-1 focus:ring-1 h-8 "
                  placeholder={"e.g 10"}
                  register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.dose`, {
                    required: true,
                  })}
                  onKeyDown={(e) => handleKeyPress(e, doseRef)}
                />
              )}

              <div>
                <InputWithSuggestionBox
                  dropdownZIndex={dropdownZIndex}
                  showAllSuggestionsOnFocus={true}
                  ref={doseRef}
                  suggestions={unitItems.map((item) => item.title)}
                  placeholder="Dose Unit"
                  defaultInputValue={formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.dose_unit`)}
                  formErrors={
                    !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.dose_unit
                  }
                  onInputChange={(value) => {
                    formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.dose_unit`, value);
                    onProgrammaticEdit?.();
                  }}
                  onKeyDown={(e) => handleKeyPress(e, frequencyRef)}
                />
              </div>
            </div>
          </div>
        </td>
        <td className={`text-left border-none  w-44`}>
          <div className="pt-2 pb-2">
            <div className="relative rounded-md">
              <div className="inset-y-0 right-0 flex">
                <div className="pl-3">
                  <InputWithSuggestionBox
                    dropdownZIndex={dropdownZIndex}
                    showAllSuggestionsOnFocus={true}
                    ref={frequencyRef}
                    suggestions={frequencyItems.map((item) => item.title)}
                    placeholder="Frequency"
                    defaultInputValue={formHook.getValues(
                      `medications.${nestedIndex}.tapering_doses.${index}.frequency`,
                    )}
                    formErrors={
                      !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.frequency
                    }
                    onInputChange={(value) => {
                      formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.frequency`, value);
                      onProgrammaticEdit?.();
                    }}
                    onKeyDown={(e) => handleKeyPress(e, timingRef)}
                  />
                </div>
              </div>
            </div>
          </div>
        </td>
        <td className=" w-44">
          <div className="pt-2 pb-2">
            <div>
              <div className="relative rounded-md">
                <div className="inset-y-0 right-0 flex">
                  {mode === "PDF_PREVIEW" ? (
                    <span className="text-xs w-32">
                      {formHook.getValues(`medications.${nestedIndex}.tapering_doses.${index}.timing`)}
                    </span>
                  ) : (
                    <div className="pl-3">
                      <InputWithSuggestionBox
                        dropdownZIndex={dropdownZIndex}
                        showAllSuggestionsOnFocus={true}
                        ref={timingRef}
                        suggestions={timingsItems.map((item) => item.title)}
                        placeholder="Timing"
                        defaultInputValue={formHook.getValues(
                          `medications.${nestedIndex}.tapering_doses.${index}.timing`,
                        )}
                        formErrors={
                          !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.timing
                        }
                        onInputChange={(value) => {
                          formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.timing`, value);
                          onProgrammaticEdit?.();
                        }}
                        onKeyDown={(e) => handleKeyPress(e, durationUnitRef)}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </td>
        <td className={`text-left  w-56`}>
          <div className="pt-2 pb-2 pl-2">
            <div className="">
              <div className="relative rounded-md flex gap-3  px-3">
                <Input
                  errorMessages={[{ message: "", type: "required" }, forNumericWithDecimal.errors]}
                  fieldError={
                    formHook?.formState?.errors
                      ? formHook?.formState?.errors?.medications
                        ? formHook?.formState?.errors?.medications?.[nestedIndex]
                          ? formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses
                            ? formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]
                              ? formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]
                                  ?.duration
                                ? formHook?.formState?.errors?.medications?.[index]?.duration
                                : null
                              : null
                            : null
                          : null
                        : null
                      : null
                  }
                  className="w-24"
                  variant="TRANSPARENT"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  inputClassName="ring-1 focus:border-1 focus:ring-1 h-8"
                  placeholder={"e.g 5"}
                  register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.duration`, {
                    required: true,
                    ...forNumericWithDecimal.validations,
                  })}
                />

                <div className="flex items-center">
                  <InputWithSuggestionBox
                    dropdownZIndex={dropdownZIndex}
                    ref={durationUnitRef}
                    suggestions={durationItems.map((item) => item.title)}
                    placeholder="Duration"
                    defaultInputValue={formHook.getValues(
                      `medications.${nestedIndex}.tapering_doses.${index}.duration_unit`,
                    )}
                    formErrors={
                      !!formHook?.formState?.errors?.medications?.[nestedIndex]?.tapering_doses?.[index]?.duration_unit
                    }
                    onInputChange={(value) => {
                      formHook.setValue(`medications.${nestedIndex}.tapering_doses.${index}.duration_unit`, value);
                      onProgrammaticEdit?.();
                    }}
                    onKeyDown={(e) => handleKeyPress(e, remarkRef)}
                  />
                </div>
              </div>
            </div>
          </div>
        </td>

        <td className={`text-left   w-40`}>
          <div className="w-full  ">
            <Input
              errorMessages={[{ message: "Temperature is required.", type: "required" }]}
              fieldError={formHook.formState.errors.medications}
              register={formHook.register(`medications.${nestedIndex}.tapering_doses.${index}.remark`, {
                required: false,
              })}
              inputClassName="text-xs"
              type="text"
              placeholder="Remarks"
            />
          </div>
        </td>
        <td className="p-2 text-center">
          <div className="w-28 -mt-2">
            <InputWithStartAndEndAddOn
              startAddon="-"
              startAddOnCallback={() => {
                if (formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.quantity`) !== 1) {
                  formHook.setValue(
                    `medications.${nestedIndex}.tapering_doses.${index}.quantity`,
                    formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.quantity`) - 1,
                  );
                  onProgrammaticEdit?.();
                }
              }}
              step={1}
              endAddOnCallback={() => {
                formHook.setValue(
                  `medications.${nestedIndex}.tapering_doses.${index}.quantity`,
                  formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.quantity`) + 1,
                );
                onProgrammaticEdit?.();
              }}
              min={1}
              endAddon="+"
              value={formHook.watch(`medications.${nestedIndex}.tapering_doses.${index}.quantity`)}
              onChange={(e: any) => {
                formHook.setValue(
                  `medications.${nestedIndex}.tapering_doses.${index}.quantity`,
                  toNumber(e.target.value),
                );
                onProgrammaticEdit?.();
              }}
              type="number"
            />
          </div>
        </td>
        <td>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              fieldArrayHook.remove(index);
              onProgrammaticEdit?.();
            }}
            className="w-8 h-8 flex items-center justify-center bg-[#F2F2F2] rounded-[10px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="white"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-5 text-[#ED0101]"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
              />
            </svg>
          </button>
        </td>
      </tr>
    );
  });
};

export default NestedTaperingDose;
